import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import React from "react";
import Form from "src/components/base/form";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Fieldset } from "@headlessui/react";
import { Description, ErrorMessage, Field, FieldGroup, Label } from "src/components/base/fieldset";
import { Switch, SwitchField } from "src/components/base/switch";
import { Input } from "src/components/base/input";
import { BackButton, Button, PrimaryButton } from "src/components/base/button";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { Subheading } from "src/components/base/heading";
import { toast } from "react-toastify";

/**
 * The properties for {@link OidcSettings}
 */
type OidcSettingsProps = {};

/**
 * The OIDC settings page
 */
function OidcSettings(props: OidcSettingsProps) {
    const [t] = useTranslation("admin-settings");

    const router = useRouter();
    const settings = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            enabled: settings.customer_tailscale.enabled,
            issuer_url: settings.customer_tailscale.url,
            client_id: settings.customer_tailscale.client_id,
            client_secret: settings.customer_tailscale.client_secret,
            redirect_url: settings.customer_tailscale.redirect_url,
        },
        onSubmit: async ({ value }) => {
            await ApiInternal.settings.updateOidc({
                customer_tailscale: {
                    client_id: value.client_id,
                    client_secret: value.client_secret,
                    enabled: value.enabled,
                    redirect_url: value.redirect_url,
                    url: value.issuer_url,
                },
            });
            form.reset();
            router.invalidate();
            toast.success(t("toast.successfully-updated"));
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <Form className={"flex max-w-lg flex-col gap-6"} onSubmit={form.handleSubmit}>
            <BackButton className={"self-start"} href={"/a/settings/oidc"}>
                {t("button.back-to-oidc-settings")}
            </BackButton>
            <Subheading>{t("heading.tailscale-oidc")}</Subheading>
            <Fieldset>
                <FieldGroup>
                    <SwitchField className={"mb-4"}>
                        <Label>{t("label.enable-oidc")}</Label>
                        <Switch
                            color={"blue"}
                            checked={settings.customer_tailscale.enabled}
                            onChange={async (enabled) => {
                                await ApiInternal.settings.updateOidc({
                                    customer_tailscale: {
                                        ...settings.customer_tailscale,
                                        enabled,
                                    },
                                });
                                router.invalidate();
                            }}
                        />
                    </SwitchField>
                </FieldGroup>
                <FieldGroup>
                    <form.Field name={"issuer_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.issuer-url")}</Label>
                                <Description>{t("description.issuer-url")}</Description>
                                <Input
                                    type={"url"}
                                    placeholder={"https://example.com"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"client_id"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.client-id")}</Label>
                                <Description>{t("description.client-id")}</Description>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"client_secret"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.client-secret")}</Label>
                                <Description>{t("description.client-secret")}</Description>
                                <Input
                                    type={"password"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"redirect_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.redirect-url")}</Label>
                                <Description>{t("description.redirect-url")}</Description>
                                <Input
                                    type={"url"}
                                    placeholder={"https://ztna.example.com/api/frontend/v1/oidc/finish-login"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                </FieldGroup>
            </Fieldset>
            <div className={"flex justify-end gap-3"}>
                <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                    {t("button.reset")}
                </Button>
                <PrimaryButton type="submit"> {t("button.save")}</PrimaryButton>
            </div>
            <BlockUnsavedChanges condition={isDirty} />
        </Form>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/oidc/customer/vpn")({
    component: OidcSettings,
    loader: async () => await ApiInternal.settings.getOidc(),
});
